import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import Button from 'shared/components/UC/Button';
import BackLink from '../components/BackLink';
import PageHeader from '../components/PageHeader';
import Modal from 'shared/components/UC/Modal';
import ProgressTracker from '../components/ProgressTracker';
import Toast from 'shared/components/Toast';
import EvaluationStep from '../components/evaluation/EvaluationStep';
import InstructionsStep from '../components/evaluation/InstructionsStep';
import SummaryStep from '../components/evaluation/SummaryStep';
import evaluatorService from '../services/evaluatorService';
import {
  CreateEvaluationRequest,
  Evaluation,
  EvaluatorApplicationDetails,
} from '../types/evaluator';
import { formatSavedAt } from '../utils/formatSavedAt';
import styles from './EvaluationInterface.module.css';
import { checkEvaluationStepCompletion } from '../utils/sectionCompletion';

interface EvaluationFormData {
  criteriaEvaluations: {
    [criteriaKey: string]: {
      score: number | null;
      comments: string | null;
    };
  };
  budgetComments: string | null;
  overallComments: string | null;
  conflictDeclaration: 'no_conflict' | 'has_conflict' | null;
  conflictsDeclared: boolean;
  conflictDetails?: string | null;
}

// Conflict declaration is no longer a step: it happens on the invitation
// landing, before the evaluator accepts. A late-discovered conflict is
// reported via the escape hatch inside the evaluation step.
const evaluationSteps = [
  { id: 'instructions', title: 'Instrucciones' },
  { id: 'evaluation', title: 'Evaluación' },
  { id: 'summary', title: 'Resumen' },
];

const EvaluationInterface: React.FC = () => {
  const { id: applicationId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [application, setApplication] =
    useState<EvaluatorApplicationDetails | null>(null);
  const [existingEvaluation, setExistingEvaluation] =
    useState<Evaluation | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // Momento y origen del último guardado (el texto de la barra y el toast
  // distinguen guardado automático vs. manual — feedback honesto).
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [lastSaveSource, setLastSaveSource] = useState<'auto' | 'manual'>('auto');
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);
  const [autoSaveInterval, setAutoSaveInterval] =
    useState<NodeJS.Timeout | null>(null);
  const [conflictModalOpen, setConflictModalOpen] = useState(false);
  const [conflictDetailsText, setConflictDetailsText] = useState('');
  const [reportingConflict, setReportingConflict] = useState(false);
  // Helper function to determine which steps are accessible
  const getAccessibleSteps = () => {
    if (!application) return [0]; // Only instructions if no application loaded

    const accessible = [0, 1]; // Instructions and Evaluation always accessible

    // Summary accessible when evaluation exists (regardless of submission status)
    if (existingEvaluation) {
      accessible.push(2);
    }

    return accessible;
  };

  // Form setup with default values
  const formMethods = useForm<EvaluationFormData>({
    mode: 'onBlur',
    defaultValues: {
      criteriaEvaluations: {},
      budgetComments: null,
      overallComments: null,
      conflictDeclaration: null,
      conflictsDeclared: false,
      conflictDetails: null,
    },
  });

  const {
    watch,
    reset,
    formState: { isDirty },
  } = formMethods;

  // Use react-hook-form's built-in isDirty
  const currentFormData = watch();

  // Load application and existing evaluation
  useEffect(() => {
    const loadData = async () => {
      if (!applicationId) {
        navigate('/evaluator/dashboard');
        return;
      }

      try {
        setLoading(true);

        // Load application details
        const appDetails =
          await evaluatorService.getApplicationDetails(applicationId);

        // Gate by assignment status: the step-by-step flow only exists for
        // accepted assignments. Pending invitations are answered on the
        // invitation landing; declined/conflicted assignments have no flow.
        const assignmentStatus = appDetails.assignment?.status;
        if (assignmentStatus === 'pending') {
          navigate(`/evaluator/invitation/${appDetails.assignment.id}`, {
            replace: true,
          });
          return;
        }
        if (
          assignmentStatus === 'declined' ||
          assignmentStatus === 'conflict_declared'
        ) {
          navigate('/evaluator/dashboard', { replace: true });
          return;
        }
        setApplication(appDetails);

        // Load existing evaluation if it exists
        const evaluation = await evaluatorService.getEvaluation(applicationId);
        setExistingEvaluation(evaluation);

        // Populate form with existing evaluation data
        let formData;
        if (evaluation) {
          formData = {
            criteriaEvaluations: evaluation.criteriaEvaluations,
            budgetComments: evaluation.budgetComments,
            overallComments: evaluation.overallComments,
            conflictDeclaration: 'no_conflict' as const, // Declared on the invitation landing
            conflictsDeclared: evaluation.conflictsDeclared,
            conflictDetails: evaluation.conflictDetails,
          };
          reset(formData);
        }

        // Determine initial step after all data is loaded
        if (evaluation?.submittedAt) {
          // If evaluation is already submitted, go to summary step
          setCurrentStep(2);
        } else if (
          evaluation &&
          checkEvaluationStepCompletion('evaluation', formData)
        ) {
          // Evaluation complete but not submitted - go to summary
          setCurrentStep(2);
        } else if (evaluation) {
          // Evaluation in progress - resume it
          setCurrentStep(1);
        } else {
          // Fresh start - instructions first
          setCurrentStep(0);
        }
      } catch (error) {
        console.error('Error loading evaluation data:', error);
        setToast({
          message: 'Error al cargar los datos de evaluación',
          type: 'error',
        });
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [applicationId, navigate, reset]);

  // Auto-save functionality
  const saveEvaluation = useCallback(
    async (data: EvaluationFormData, source: 'auto' | 'manual' = 'auto') => {
      if (!applicationId || !isDirty) return;

      try {
        setSaving(true);

        // Transform form data to match backend API expectations
        const transformedCriteriaEvaluations: Record<string, any> = {};
        Object.entries(data.criteriaEvaluations).forEach(([key, value]) => {
          if (value.score !== null && value.comments !== null) {
            transformedCriteriaEvaluations[key] = {
              score: value.score,
              comments: value.comments,
              maxScore: 5, // Fixed max score for all criteria
            };
          }
        });

        const evaluationData: CreateEvaluationRequest = {
          criteriaEvaluations: transformedCriteriaEvaluations,
          budgetComments: data.budgetComments || null,
          overallComments: data.overallComments,
          conflictsDeclared: data.conflictsDeclared,
          conflictDetails: data.conflictDetails || null,
        };

        if (existingEvaluation) {
          await evaluatorService.updateEvaluation(
            applicationId,
            evaluationData
          );
        } else {
          const newEvaluation = await evaluatorService.createEvaluation(
            applicationId,
            evaluationData
          );
          setExistingEvaluation(newEvaluation);
        }

        // Reset form dirty state while keeping values
        reset(data, { keepValues: true });
        setLastSavedAt(new Date());
        setLastSaveSource(source);

        // Show success toast — honesto con el origen del guardado
        setToast({
          message:
            source === 'manual'
              ? 'Cambios guardados'
              : 'Evaluación guardada automáticamente',
          type: 'success',
        });
      } catch (error) {
        console.error('Error saving evaluation:', error);
        setToast({
          message: 'Error al guardar la evaluación',
          type: 'error',
        });
      } finally {
        setSaving(false);
      }
    },
    [applicationId, existingEvaluation, isDirty, reset]
  );

  // Use refs to avoid stale closures without causing re-renders
  const watchRef = useRef(watch);
  const saveEvaluationRef = useRef(saveEvaluation);

  // Update refs on every render
  watchRef.current = watch;
  saveEvaluationRef.current = saveEvaluation;

  // Auto-save setup - only set up once when entering evaluation step
  useEffect(() => {
    if (currentStep === 1 && !loading) {
      // Only auto-save during evaluation step
      // Clear any existing interval first
      if (autoSaveInterval) {
        clearInterval(autoSaveInterval);
      }

      // Set new interval for auto-save every 30 seconds
      const interval = setInterval(() => {
        const currentFormData = watchRef.current();
        saveEvaluationRef.current(currentFormData);
      }, 30000);

      setAutoSaveInterval(interval);

      return () => {
        clearInterval(interval);
      };
    } else if (autoSaveInterval) {
      // Clear interval when not in evaluation step
      clearInterval(autoSaveInterval);
      setAutoSaveInterval(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, loading]);

  useEffect(() => {
    if (window.scrollY > 460) {
      window.scrollTo({ top: 360, behavior: 'smooth' });
    }
  }, [currentStep]);

  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      if (autoSaveInterval) {
        clearInterval(autoSaveInterval);
      }
    };
  }, [autoSaveInterval]);

  const handleStepClick = async (stepIndex: number) => {
    // If evaluation is submitted, only allow summary step
    if (existingEvaluation?.submittedAt && stepIndex !== 2) {
      return;
    }

    // Check if the target step is accessible
    const accessibleSteps = getAccessibleSteps();
    if (!accessibleSteps.includes(stepIndex)) {
      return; // Don't navigate to inaccessible steps
    }

    // Save before moving from evaluation step to summary
    if (currentStep === 1 && stepIndex === 2) {
      const currentFormData = watch();
      await saveEvaluation(currentFormData);
    }

    // Navigate to the target step
    setCurrentStep(stepIndex);
  };

  const handleNextStep = async () => {
    if (currentStep === 1) {
      // Save before moving to summary
      const currentFormData = watch();
      await saveEvaluation(currentFormData);
    }

    // Check if next step is accessible
    const nextStep = currentStep + 1;
    if (
      nextStep < evaluationSteps.length &&
      getAccessibleSteps().includes(nextStep)
    ) {
      setCurrentStep(nextStep);
    }
  };

  const handlePreviousStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Escape hatch: a conflict discovered mid-evaluation. Registers the
  // conflict (same unified mechanism as the invitation landing) and closes
  // the evaluation flow for this assignment.
  const handleReportConflict = async () => {
    if (!applicationId || !conflictDetailsText.trim()) return;
    setReportingConflict(true);
    try {
      await evaluatorService.declareConflictStatus(applicationId, {
        hasConflict: true,
        conflictDetails: conflictDetailsText.trim(),
      });
      navigate('/evaluator/dashboard', { replace: true });
    } catch {
      setToast({
        message:
          'Error al registrar el conflicto. Por favor intente nuevamente.',
        type: 'error',
      });
      setReportingConflict(false);
    }
  };

  const renderCurrentStep = () => {
    if (!application) {
      return <div>Cargando aplicación...</div>;
    }

    switch (currentStep) {
      case 0:
        return <InstructionsStep onStart={() => setCurrentStep(1)} />;
      case 1:
        return (
          <EvaluationStep
            application={application}
            evaluation={existingEvaluation}
          />
        );
      case 2:
        return (
          <SummaryStep
            application={application}
            evaluation={existingEvaluation}
            onSubmit={async () => {
              try {
                await evaluatorService.submitEvaluation(applicationId!);
                setToast({
                  message: 'Evaluación enviada exitosamente',
                  type: 'success',
                });
                // Reload evaluation to get updated state
                const updatedEvaluation = await evaluatorService.getEvaluation(
                  applicationId!
                );
                setExistingEvaluation(updatedEvaluation);
              } catch (error) {
                setToast({
                  message: 'Error al enviar la evaluación',
                  type: 'error',
                });
              }
            }}
          />
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>
          <h2>Cargando evaluación...</h2>
          <p>Por favor espere mientras se cargan los datos.</p>
        </div>
      </div>
    );
  }

  if (!application) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>
          <h2>Aplicación no encontrada</h2>
          <p>
            La aplicación solicitada no existe o no tienes permisos para
            evaluarla.
          </p>
          <BackLink
            to="postulaciones asignadas"
            onClick={() => navigate('/evaluator/dashboard')}
          />
        </div>
      </div>
    );
  }

  return (
    <FormProvider {...formMethods}>
      <div className={styles.pageWrapper}>
        <PageHeader
          sticky
          titleLevel="detail"
          title="Evaluación de Postulación"
          backLink={{
            to: 'postulaciones asignadas',
            onClick: () => navigate('/evaluator/dashboard'),
          }}
        />

        <div className={styles.container}>

        {/* El stepper es la navegación del asistente de 3 pasos; en una
            evaluación enviada (solo lectura, se entra desde "Ver Evaluación")
            no hay nada que recorrer y además confundía (mostraba "Evaluación"
            con "!" de pendiente). El estado enviado se indica en el encabezado
            del Resumen (sello verde). */}
        {!existingEvaluation?.submittedAt && (
          <ProgressTracker
            steps={evaluationSteps}
            currentStep={currentStep}
            onStepClick={handleStepClick}
            isEvaluation={true}
            evaluationSubmitted={false}
          />
        )}

        <div className={styles.content}>{renderCurrentStep()}</div>

        {/* Navigation buttons */}
        {currentStep > 0 && !existingEvaluation?.submittedAt && (
          <div className={styles.navigation}>
            <div className={styles.navGroup}>
              <Button
                variant="secondary"
                onClick={handlePreviousStep}
                disabled={currentStep === 0}
              >
                Anterior
              </Button>
              {currentStep === 1 && (
                <Button
                  variant="primary"
                  onClick={() => setConflictModalOpen(true)}
                  icon="open_in_new"
                  iconPos="right"
                >
                  Reportar conflicto de interés
                </Button>
              )}
            </div>
            <div className={styles.navGroup}>
              {currentStep === 1 && (
                <div className={styles.saveCluster}>
                  <span
                    className={`${styles.saveStatus} ${
                      saving
                        ? styles.saveStatusSaving
                        : isDirty
                          ? styles.saveStatusDirty
                          : styles.saveStatusSaved
                    }`}
                  >
                    {!isDirty || saving ? (
                      <span
                        className={`${styles.saveIcon} ${
                          saving ? styles.saveIconSaving : ''
                        }`}
                        aria-hidden="true"
                      >
                        {saving ? '⟳' : '✓'}
                      </span>
                    ) : null}
                    {(() => {
                      if (saving) return 'Guardando...';
                      if (isDirty) return 'Cambios sin guardar';
                      // Homologado con el form IR: incluye el momento del
                      // guardado (sesión actual o updatedAt de la evaluación).
                      const savedAt =
                        lastSavedAt ??
                        (existingEvaluation?.updatedAt
                          ? new Date(existingEvaluation.updatedAt)
                          : null);
                      const label =
                        lastSaveSource === 'manual'
                          ? 'Cambios guardados'
                          : 'Guardado automáticamente';
                      return savedAt
                        ? `${label} · ${formatSavedAt(savedAt)}`
                        : label;
                    })()}
                  </span>
                  <Button
                    variant="primary"
                    onClick={() => saveEvaluation(currentFormData, 'manual')}
                    disabled={saving || !isDirty}
                  >
                    {saving ? 'Guardando...' : 'Guardar cambios'}
                  </Button>
                </div>
              )}
              {currentStep < 2 && (
                <Button
                  variant="primary"
                  onClick={handleNextStep}
                  disabled={
                    currentStep === evaluationSteps.length - 1 ||
                    !getAccessibleSteps().includes(currentStep + 1)
                  }
                >
                  Siguiente
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Escape hatch: conflict discovered mid-evaluation */}
        <Modal
          isOpen={conflictModalOpen}
          onClose={() => {
            if (!reportingConflict) setConflictModalOpen(false);
          }}
          title="Reportar conflicto de interés"
          size="md"
        >
          <div className={styles.conflictModalBody}>
            <p className="uc-p-body">
              Si durante la evaluación advirtió una relación con algún
              integrante del equipo que compromete su imparcialidad, repórtela
              aquí. Al confirmar, la evaluación quedará cerrada para usted y la
              Dirección de Investigación asignará otro evaluador.
            </p>
            <label className={styles.conflictModalLabel}>
              Describa el conflicto{' '}
              <span className={styles.conflictModalRequired}>*</span>
            </label>
            <textarea
              className={styles.conflictModalTextarea}
              rows={4}
              placeholder="Indique con qué integrante existe la relación y de qué tipo es."
              value={conflictDetailsText}
              onChange={(e) => setConflictDetailsText(e.target.value)}
              disabled={reportingConflict}
            />
            <div className={styles.conflictModalActions}>
              <Button
                variant="secondary"
                onClick={() => setConflictModalOpen(false)}
                disabled={reportingConflict}
              >
                Cancelar
              </Button>
              <Button
                variant="primary"
                onClick={handleReportConflict}
                disabled={reportingConflict || !conflictDetailsText.trim()}
              >
                {reportingConflict ? 'Registrando...' : 'Confirmar conflicto'}
              </Button>
            </div>
          </div>
        </Modal>

        {/* Toast notifications */}
        {toast && (
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={() => setToast(null)}
          />
        )}
        </div>
      </div>
    </FormProvider>
  );
};

export default EvaluationInterface;
